/**
 * @copyright 2026 Cooltrax Pty Ltd. All rights reserved.
 */

import { createContext, useCallback, useContext, useMemo, useState } from 'react'

import { useQueryClient } from '@tanstack/react-query'

import { Toast, type ToastProps } from '@/components/ui'
import { useApi, useSessionStorage, useToast } from '@/hooks'
import type { SavedView } from '@/types/dashboard-types'

import { putLayout } from './api'
import { DEFAULT_LIST_WIDTH } from './columns'
import { DEFAULT_FILTERS, type ListFilters, applyFilters, applySearch } from './filters'
import { IMAGES_QUERY_KEY, useCompanyImages, useInStoreState } from './hooks'
import { autoSurfaceLocation } from './layout'
import { type ColdStorageMode, MODES, type ModeConfig } from './mode'
import {
  FLOOR_PLAN_USAGE,
  type ImageMeta,
  type InStoreAsset,
  type Marker,
  type MarkerRef,
  type Position,
  markerKey,
} from './types'

export { DEFAULT_LIST_WIDTH }

/** A saved cold storage or blast freezer view: the company, the locations picked and the filters. */
export interface ColdStorageSavedView extends SavedView {
  companyId?: number
  /** Null for every location, so locations added later show up. */
  locationIds: number[] | null
  filters: ListFilters
  listWidth: number
}

interface ColdStorageContextType {
  /** Which screen this is: cold storage, or blast freezer. */
  mode: ModeConfig
  companyId?: number
  setCompanyId: (id: number | undefined) => void
  /** Every in-store asset of the company. */
  assets: InStoreAsset[]
  /**
   * The ones picked in the Location filter that pass the Gateway filter and the search, in list
   * order, each with only the sensors that pass the Status filter (and the search, unless the
   * location's own name matched).
   */
  locations: InStoreAsset[]
  searchTerm: string
  setSearchTerm: (term: string) => void
  locationIds: number[]
  setLocationIds: (ids: number[]) => void
  filters: ListFilters
  setFilters: (filters: ListFilters) => void
  isLoading: boolean
  error?: string
  updatedAt?: Date
  /** A fetch of the state is in flight: the first load or a poll. */
  isRefreshing: boolean
  images: ImageMeta[]
  /** The floor plan a location shows, if it has one. */
  imageFor: (assetId: number) => ImageMeta | undefined
  selectedLocationId?: number
  /**
   * The location whose surface the pane shows: the clicked one while it is listed, else
   * the one `autoSurfaceLocation` picks (the only location, or the only floor plan).
   */
  surfaceLocationId?: number
  /** Shows the location's floor plan, closing any details over it. */
  selectLocation: (id: number | undefined) => void
  /** Opens the location's details (graph and alerts) in the sheet; the floor plan stays as it is. */
  openLocation: (id: number) => void
  /** The location whose details are open. Exclusive with the sensor's. */
  locationModalId?: number
  setLocationModalId: (id: number | undefined) => void
  selectedSensorId?: number
  /** Selects the sensor and opens its details in the sheet; the floor plan stays as it is. */
  selectSensor: (id: number | undefined) => void
  /** The sensor hovered in the list or on the floor plan; its marker shows its label. */
  hoveredSensorId?: number
  setHoveredSensorId: (id: number | undefined) => void
  /** The selected sensor's details are open. Exclusive with a location's. */
  showSensorModal: boolean
  setShowSensorModal: (show: boolean) => void
  /** Either detail view is open: the sheet shows, and the toolbar's second row goes away. */
  detailIsOpen: boolean
  closeDetail: () => void
  rangeLocationId?: number
  setRangeLocationId: (id: number | undefined) => void
  showFloorPlans: boolean
  setShowFloorPlans: (show: boolean) => void
  editMode: boolean
  setEditMode: (on: boolean) => void
  /** Marker positions moved in edit mode and not yet saved, by `markerKey`. */
  draft: Record<string, Position>
  setDraftPosition: (marker: MarkerRef, position: Position) => void
  dirty: boolean
  savingLayout: boolean
  saveLayout: () => Promise<void>
  /** Grid pitch in px that dragged markers snap to in edit mode; 0 for free placement. */
  snapGrid: number
  setSnapGrid: (px: number) => void
  discardLayout: () => void
  listWidth: number
  setListWidth: (width: number) => void
  notify: (options: Omit<ToastProps, 'show'>) => void
  invalidateState: () => Promise<void>
  invalidateImages: () => Promise<void>
  reset: () => void
  /** The saved view the page is showing, or null. */
  selectedView: ColdStorageSavedView | null
  setSelectedView: (view: ColdStorageSavedView | null) => void
  /** Switches the page to `view` and selects it. */
  applyView: (view: ColdStorageSavedView) => void
  isSwitchingView: boolean
  /** The page as it is now, as a view called `name`. */
  currentView: (name: string, isDefault: boolean) => ColdStorageSavedView
  /** This tab restored a company from session storage, so a default view shouldn't override it. */
  companyHydrated: boolean
}

const ColdStorageContext = createContext<ColdStorageContextType | undefined>(undefined)

const sessionKeys = (prefix: string) =>
  ({
    companyId: `${prefix}.companyId`,
    locationIds: `${prefix}.locationIds`,
    listWidth: `${prefix}.listWidth`,
    snapGrid: `${prefix}.snapGrid`,
    filters: `${prefix}.filters`,
    searchTerm: `${prefix}.searchTerm`,
  }) as const

/** The one grid pitch on offer for now; the checkbox turns it on or off. */
export const SNAP_GRID_PX = 10

/** The asset a sensor belongs to. */
export const assetOfSensor = (assets: InStoreAsset[], sensorId: number): InStoreAsset | undefined =>
  assets.find((asset) => asset.sensors.some((sensor) => sensor.id === sensorId))

/** The markers of one location whose positions are in the draft. */
export const draftMarkersOf = (asset: InStoreAsset, draft: Record<string, Position>): Marker[] => {
  const refs: MarkerRef[] = [
    ...(asset.gateway ? [{ type: 'gateway' as const, id: asset.id }] : []),
    ...asset.sensors.map((sensor) => ({ type: 'sensor' as const, id: sensor.id })),
  ]
  return refs.flatMap((ref) => {
    const position = draft[markerKey(ref)]
    return position ? [{ ...ref, ...position }] : []
  })
}

export const ColdStorageProvider: React.FC<{ mode?: ColdStorageMode; children: React.ReactNode }> = ({
  mode: modeName = 'coldStorage',
  children,
}) => {
  const mode = MODES[modeName]
  const SESSION_KEYS = sessionKeys(mode.sessionPrefix)
  const { api } = useApi()
  const queryClient = useQueryClient()
  const { toast, show, showToast, hideToast } = useToast()

  const [companyId, setCompanyIdState, companyHydrated] = useSessionStorage<number | undefined>(
    SESSION_KEYS.companyId,
    undefined
  )
  // null means "every location", so a cold room added later shows up without a re-pick.
  const [storedLocationIds, setStoredLocationIds] = useSessionStorage<number[] | null>(SESSION_KEYS.locationIds, null)
  const [listWidth, setListWidth] = useSessionStorage<number>(SESSION_KEYS.listWidth, mode.defaultListWidth)
  const [snapGrid, setSnapGrid] = useSessionStorage<number>(SESSION_KEYS.snapGrid, 0)
  const [filters, setFilters] = useSessionStorage<ListFilters>(SESSION_KEYS.filters, DEFAULT_FILTERS)
  const [searchTerm, setSearchTerm] = useSessionStorage<string>(SESSION_KEYS.searchTerm, '')
  const [selectedView, setSelectedView] = useState<ColdStorageSavedView | null>(null)
  const [isSwitchingView, setIsSwitchingView] = useState(false)

  const [selectedLocationId, setSelectedLocationId] = useState<number>()
  const [selectedSensorId, setSelectedSensorId] = useState<number>()
  const [hoveredSensorId, setHoveredSensorId] = useState<number>()
  const [showSensorModal, setShowSensorModal] = useState(false)
  const [locationModalId, setLocationModalId] = useState<number>()
  const [rangeLocationId, setRangeLocationId] = useState<number>()
  const [showFloorPlans, setShowFloorPlans] = useState(false)
  const [editMode, setEditModeState] = useState(false)
  const [draft, setDraft] = useState<Record<string, Position>>({})
  const [savingLayout, setSavingLayout] = useState(false)

  const state = useInStoreState(companyId, { paused: editMode, mode })
  const imagesQuery = useCompanyImages(companyId)
  const assets = useMemo(() => state.data ?? [], [state.data])
  const images = useMemo(() => imagesQuery.data ?? [], [imagesQuery.data])

  const locationIds = useMemo(
    () => (storedLocationIds ?? assets.map((asset) => asset.id)).filter((id) => assets.some((a) => a.id === id)),
    [storedLocationIds, assets]
  )
  const locations = useMemo(
    () =>
      applySearch(
        applyFilters(
          assets.filter((asset) => locationIds.includes(asset.id)),
          filters
        ),
        searchTerm
      ),
    [assets, locationIds, filters, searchTerm]
  )

  const imageFor = useCallback(
    (assetId: number) =>
      images.find((image) =>
        image.links.some((l) => l.entityType === 'asset' && l.entityId === assetId && l.usage === FLOOR_PLAN_USAGE)
      ),
    [images]
  )

  const surfaceLocationId = useMemo(() => {
    if (selectedLocationId != null && locations.some((asset) => asset.id === selectedLocationId)) {
      return selectedLocationId
    }
    return autoSurfaceLocation(locations, imageFor)
  }, [selectedLocationId, locations, imageFor])

  const setCompanyId = useCallback(
    (id: number | undefined) => {
      setCompanyIdState(id)
      setStoredLocationIds(null)
      setSelectedLocationId(undefined)
      setSelectedSensorId(undefined)
      setShowSensorModal(false)
      setLocationModalId(undefined)
      setDraft({})
      setEditModeState(false)
    },
    [setCompanyIdState, setStoredLocationIds]
  )

  // The sheet shows one thing at a time: a location's details replace a sensor's, and back.
  const openLocation = useCallback((id: number) => {
    setShowSensorModal(false)
    setLocationModalId(id)
  }, [])

  const selectSensor = useCallback((id: number | undefined) => {
    setSelectedSensorId(id)
    if (id != null) {
      setLocationModalId(undefined)
      setShowSensorModal(true)
    }
  }, [])

  const closeDetail = useCallback(() => {
    setShowSensorModal(false)
    setLocationModalId(undefined)
  }, [])

  // Picking a location is asking for its floor plan, so the details sheet over it goes.
  const selectLocation = useCallback(
    (id: number | undefined) => {
      setSelectedLocationId(id)
      setSelectedSensorId(undefined)
      closeDetail()
    },
    [closeDetail]
  )

  const setDraftPosition = useCallback((marker: MarkerRef, position: Position) => {
    setDraft((prev) => ({ ...prev, [markerKey(marker)]: position }))
  }, [])

  const setEditMode = useCallback(
    (on: boolean) => {
      setEditModeState(on)
      // Editing needs the floor plan the sheet would cover.
      if (on) closeDetail()
      else setDraft({})
    },
    [closeDetail]
  )

  const discardLayout = useCallback(() => setEditMode(false), [setEditMode])

  const invalidateState = useCallback(
    () => queryClient.invalidateQueries({ queryKey: [mode.stateQueryKey, companyId] }),
    [queryClient, mode.stateQueryKey, companyId]
  )
  const invalidateImages = useCallback(
    () => queryClient.invalidateQueries({ queryKey: [IMAGES_QUERY_KEY, companyId] }),
    [queryClient, companyId]
  )

  // One PUT per location whose markers moved: the API scopes a layout to an asset.
  const saveLayout = useCallback(async () => {
    const saves = assets
      .map((asset) => ({ asset, markers: draftMarkersOf(asset, draft) }))
      .filter(({ markers }) => markers.length > 0)
    setSavingLayout(true)
    try {
      await Promise.all(saves.map(({ asset, markers }) => putLayout(api, asset.id, markers)))
      await invalidateState()
      setEditMode(false)
      showToast({
        header: 'Layout saved',
        body: 'The markers will show where you left them.',
        position: 'end',
        success: true,
      })
    } catch {
      showToast({
        header: 'Layout not saved',
        body: 'Something went wrong. Please try again.',
        position: 'end',
        warning: true,
      })
    } finally {
      setSavingLayout(false)
    }
  }, [assets, draft, api, invalidateState, setEditMode, showToast])

  const reset = useCallback(() => {
    setStoredLocationIds(null)
    setFilters(DEFAULT_FILTERS)
    setSelectedLocationId(undefined)
    setSelectedSensorId(undefined)
    setShowSensorModal(false)
    setLocationModalId(undefined)
    setEditMode(false)
    setListWidth(mode.defaultListWidth)
    setSearchTerm('')
    setSelectedView(null)
  }, [setStoredLocationIds, setFilters, setEditMode, setListWidth, setSearchTerm, mode.defaultListWidth])

  const currentView = useCallback(
    (name: string, isDefault: boolean): ColdStorageSavedView => ({
      name,
      isDefault,
      companyId,
      locationIds: storedLocationIds,
      filters,
      listWidth,
    }),
    [companyId, storedLocationIds, filters, listWidth]
  )

  const applyView = useCallback(
    (view: ColdStorageSavedView) => {
      setIsSwitchingView(true)
      setSelectedView(view)
      setCompanyIdState(view.companyId)
      setStoredLocationIds(view.locationIds)
      setFilters({ ...DEFAULT_FILTERS, ...view.filters })
      setListWidth(view.listWidth || mode.defaultListWidth)
      setSelectedLocationId(undefined)
      setSelectedSensorId(undefined)
      closeDetail()
      setDraft({})
      setEditModeState(false)
      setTimeout(() => setIsSwitchingView(false), 500)
    },
    [setCompanyIdState, setStoredLocationIds, setFilters, setListWidth, closeDetail, mode.defaultListWidth]
  )

  const value = useMemo<ColdStorageContextType>(
    () => ({
      mode,
      companyId,
      setCompanyId,
      assets,
      locations,
      searchTerm,
      setSearchTerm,
      locationIds,
      setLocationIds: setStoredLocationIds,
      filters,
      setFilters,
      isLoading: state.isLoading,
      error: state.error ? 'Unable to load cold storage locations.' : undefined,
      updatedAt: state.dataUpdatedAt ? new Date(state.dataUpdatedAt) : undefined,
      isRefreshing: state.isFetching,
      images,
      imageFor,
      selectedLocationId,
      surfaceLocationId,
      selectLocation,
      openLocation,
      locationModalId,
      setLocationModalId,
      selectedSensorId,
      selectSensor,
      hoveredSensorId,
      setHoveredSensorId,
      showSensorModal,
      setShowSensorModal,
      detailIsOpen: locationModalId != null || (showSensorModal && selectedSensorId != null),
      closeDetail,
      rangeLocationId,
      setRangeLocationId,
      showFloorPlans,
      setShowFloorPlans,
      editMode,
      setEditMode,
      draft,
      setDraftPosition,
      dirty: Object.keys(draft).length > 0,
      savingLayout,
      saveLayout,
      snapGrid,
      setSnapGrid,
      discardLayout,
      listWidth,
      setListWidth,
      notify: showToast,
      invalidateState,
      invalidateImages,
      reset,
      selectedView,
      setSelectedView,
      applyView,
      isSwitchingView,
      currentView,
      companyHydrated,
    }),
    [
      mode,
      companyId,
      setCompanyId,
      assets,
      locations,
      locationIds,
      setStoredLocationIds,
      filters,
      setFilters,
      state.isLoading,
      state.error,
      state.dataUpdatedAt,
      state.isFetching,
      images,
      imageFor,
      selectedLocationId,
      surfaceLocationId,
      selectLocation,
      openLocation,
      locationModalId,
      selectedSensorId,
      selectSensor,
      hoveredSensorId,
      showSensorModal,
      rangeLocationId,
      showFloorPlans,
      editMode,
      setEditMode,
      draft,
      setDraftPosition,
      savingLayout,
      saveLayout,
      snapGrid,
      setSnapGrid,
      discardLayout,
      listWidth,
      setListWidth,
      showToast,
      invalidateState,
      invalidateImages,
      reset,
      searchTerm,
      setSearchTerm,
      closeDetail,
      selectedView,
      applyView,
      isSwitchingView,
      currentView,
      companyHydrated,
    ]
  )

  return (
    <ColdStorageContext.Provider value={value}>
      {children}
      {/* One Toast for the page, so every part of it can notify without owning its own. */}
      <Toast {...toast} show={show} onClose={hideToast} />
    </ColdStorageContext.Provider>
  )
}

export const useColdStorage = (): ColdStorageContextType => {
  const context = useContext(ColdStorageContext)
  if (!context) throw new Error('useColdStorage must be used within ColdStorageProvider')
  return context
}
